import { ConnectButton } from '@rainbow-me/rainbowkit';
import { AppSidebar } from "@/components/app-sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/breadcrumb";
import { Separator } from "@/components/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/sidebar";
import { Link } from "react-router-dom";
import { RiGlobalLine, RiLoader4Line } from "@remixicon/react";
import { useSilo } from "@/providers/global-data.provider";
import { StorageChart } from "@/components/storage-chart";
import { CountryList } from "@/components/country-list";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/card";

export default function SiloStatsPage() {
  const {
    totalSpace,
    usedSpace,
    freeSpace,
    activeNodes,
    networkUptime,
    dataTransfer,
    storageCost,
    isLoading,
  } = useSilo();

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <div className="px-4 md:px-6 lg:px-8 @container">
          <div className="w-full max-w-6xl mx-auto">
            <header className="flex flex-wrap gap-3 min-h-20 py-4 shrink-0 items-center transition-all ease-linear border-b">
              <div className="flex flex-1 items-center gap-2">
                <SidebarTrigger className="-ms-1" />
                <div className="max-lg:hidden lg:contents">
                  <Separator
                    orientation="vertical"
                    className="me-2 data-[orientation=vertical]:h-4"
                  />
                  <Breadcrumb>
                    <BreadcrumbList>
                      <BreadcrumbItem className="hidden md:block">
                        <BreadcrumbLink asChild>
                          <Link to="/">Home</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                          <Link to="/silo">SILO</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbPage>Stats</BreadcrumbPage>
                      </BreadcrumbItem>
                    </BreadcrumbList>
                  </Breadcrumb>
                </div>
              </div>
              <ConnectButton />
            </header>

            <div className="py-6">
              <div className="flex items-center mb-6">
                <div className="w-1 h-8 bg-[#FF8A3C] mr-3"></div>
                <h1 className="text-3xl font-bold">SILO Statistics</h1>
              </div>

              <p className="text-muted-foreground mb-8 max-w-3xl">
                Explore key metrics and statistics for the SILO storage network. Monitor network health,
                storage capacity, usage, and performance.
              </p>

              <div className="w-full max-w-xs h-1 bg-gradient-to-r from-[#FF8A3C] to-transparent mb-8 rounded-full"></div>

              <div className="mb-10">
                <div className="flex items-center mb-4">
                  <div className="w-1 h-6 bg-[#FF8A3C] mr-3"></div>
                  <h2 className="text-2xl font-bold">Storage Distribution</h2>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
                  <Card className="bg-sidebar border border-border lg:col-span-1">
                    <CardContent className="p-6">
                      <div className="flex flex-col items-center justify-center h-full">
                        {isLoading ? (
                          <RiLoader4Line className="size-8 animate-spin mb-2" />
                        ) : (
                          <div className="text-4xl font-bold mb-2">{totalSpace.toFixed(2)}</div>
                        )}
                        <div className="text-sm text-muted-foreground text-center">Total Space (TB)</div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-sidebar border border-border lg:col-span-1">
                    <CardContent className="p-6">
                      <div className="flex flex-col items-center justify-center h-full">
                        {isLoading ? (
                          <RiLoader4Line className="size-8 animate-spin mb-2 text-[#FF8A3C]" />
                        ) : (
                          <div className="text-4xl font-bold mb-2 text-[#FF8A3C]">{usedSpace.toFixed(2)}</div>
                        )}
                        <div className="text-sm text-muted-foreground text-center">Used Space (TB)</div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-sidebar border border-border lg:col-span-1">
                    <CardContent className="p-6">
                      <div className="flex flex-col items-center justify-center h-full">
                        {isLoading ? (
                          <RiLoader4Line className="size-8 animate-spin mb-2" />
                        ) : (
                          <div className="text-4xl font-bold mb-2">{freeSpace.toFixed(2)}</div>
                        )}
                        <div className="text-sm text-muted-foreground text-center">Free Space (TB)</div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-sidebar border border-border lg:col-span-1">
                    <CardContent className="p-6">
                      <div className="flex flex-col items-center justify-center h-full">
                        {isLoading ? (
                          <RiLoader4Line className="size-8 animate-spin mb-2" />
                        ) : (
                          <div className="text-4xl font-bold mb-2">{activeNodes}</div>
                        )}
                        <div className="text-sm text-muted-foreground text-center">Active Nodes</div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <Card className="bg-sidebar border border-border lg:col-span-2">
                    <CardHeader>
                      <CardTitle>Storage Utilization</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <StorageChart />
                    </CardContent>
                  </Card>

                  <Card className="bg-sidebar border border-border lg:col-span-1">
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <RiGlobalLine className="mr-2 size-5 text-muted-foreground" />
                        <span>Top Countries</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CountryList />
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/*<div className="mb-10">
                <div className="flex items-center mb-4">
                  <div className="w-1 h-6 bg-[#FF8A3C] mr-3"></div>
                  <h2 className="text-2xl font-bold">Network Performance</h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  <Card className="bg-sidebar border border-border">
                    <CardContent className="p-6">
                      <div className="flex flex-col items-center justify-center h-32">
                        {isLoading ? (
                          <RiLoader4Line className="size-8 animate-spin mb-2" />
                        ) : (
                          <div className="text-4xl font-bold mb-2">{networkUptime.toFixed(1)}%</div>
                        )}
                        <div className="text-sm text-muted-foreground text-center">Network Uptime</div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-sidebar border border-border">
                    <CardContent className="p-6">
                      <div className="flex flex-col items-center justify-center h-32">
                        {isLoading ? (
                          <RiLoader4Line className="size-8 animate-spin mb-2" />
                        ) : (
                          <div className="text-4xl font-bold mb-2">{dataTransfer.toFixed(1)} TB</div>
                        )}
                        <div className="text-sm text-muted-foreground text-center">Monthly Data Transfer</div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-sidebar border border-border">
                    <CardContent className="p-6">
                      <div className="flex flex-col items-center justify-center h-32">
                        {isLoading ? (
                          <RiLoader4Line className="size-8 animate-spin mb-2" />
                        ) : (
                          <div className="text-4xl font-bold mb-2">{storageCost.toFixed(3)} LRS</div>
                        )}
                        <div className="text-sm text-muted-foreground text-center">Storage Cost per GB</div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>*/}

              {/*<div className="mt-10 p-6 bg-sidebar border border-border rounded-lg">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <span className="w-1 h-6 bg-[#FF8A3C] mr-3 inline-block"></span>
                  More Statistics Coming Soon
                </h3>
                <p className="text-muted-foreground">
                  We're working on adding more detailed statistics and visualizations for the SILO network.
                  Check back soon for updates on network growth, geographic distribution, and performance metrics.
                </p>
              </div>*/}
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}



